import React from 'react';
import { useTranslation } from 'react-i18next';
import { SourceDetails } from 'features/layout/types';
import { useIsLocallyMuted } from 'features/local-mute/useIsLocallyMuted';
import { useRemoteAudioMute } from 'hooks/useRemoteAudioMute';
import { ClickCallback } from 'features/streaming/components/controls/useTileLocalMute';

// Locally muting a tile hides MicControlRemote, so the moderator's remote-mute action
// is re-exposed here to avoid losing it while the mic button shows the mute indicator.
export const useRemoteMuteFallbackControl = (
  source: SourceDetails,
  clickCallback: ClickCallback
) => {
  const { t } = useTranslation('room');
  const isLocallyMuted = useIsLocallyMuted(source.userId);
  const {
    isAudioBroadcasting,
    toggleMicRemotely,
    canRemotelyMute,
    canAskToUnmute,
    isAskedToUnmuteAudio,
  } = useRemoteAudioMute(source.userId);

  const renderText = () => {
    if (isAskedToUnmuteAudio) {
      return t('tooltips.asked_to_unmute_remote_audio');
    }

    return isAudioBroadcasting
      ? t('tooltips.mute_remote_audio')
      : t('tooltips.unmute_remote_audio');
  };

  const renderIcon = () => {
    if (isAskedToUnmuteAudio) {
      return 'mic-clock-pending';
    }

    return isAudioBroadcasting ? 'mic' : 'mute';
  };

  return {
    show: source.kind === 'remote' && isLocallyMuted && (canRemotelyMute || canAskToUnmute),
    icon: renderIcon(),
    text: renderText(),
    onClick: (e: React.MouseEvent<HTMLButtonElement | HTMLLIElement, MouseEvent>) => {
      // Mirrors MicControlRemote.handleClick: a pending ask-to-unmute is not re-togglable.
      if (isAskedToUnmuteAudio) {
        return;
      }
      clickCallback?.(e);
      toggleMicRemotely();
    },
  };
};
